/**
 * EditorView Component - Replace Operation Implementation
 * 
 * Implements three-stage replace operation for word-level track changes:
 * 
 * STAGE 1: IDENTIFICATION
 * - Locks selection snapshot (text + OOXML) when user sends message
 * - Uses locked snapshot for all subsequent operations (comparison, apply)
 * - Optionally highlights selection in document for visual feedback
 * 
 * STAGE 2: COMPARISON / PREVIEW
 * - Default (MCC/CL Client Request): previewHybridOps against the live paragraph — the same
 *   Op[] plan Submit-to-Response will apply (their-side red, our-side blue).
 * - Legacy (Profile toggle): RobustXmlTrackChangesGenerator + convertOoxmlToHtml
 *
 * STAGE 3: APPLY
 * - Default: applyViaHybridEngine(paragraphIndex, cleanTarget) — same entry as MCC/CL
 * - Legacy: insertOoxml of generated paragraph, then incremental Range.search
 */

import * as React from 'react';
import { useTranslation } from 'react-i18next';
import { Eye, Copy, Minus, MessageCircle, PencilLine } from 'lucide-react';
import { ChatService } from '../../features/chat/ChatService';
import { useSelection } from '../hooks/useSelection';
import { ChatComposer } from './ChatComposer';
import { TrackChangesParser } from '../../word/utils/TrackChangesParser';
import { DocumentReader } from '../../word/api/DocumentReader';
import { RedlineInserter } from '../../word/trackChanges/RedlineInserter';
import { RobustXmlTrackChangesGenerator } from '../../features/rephrasing/RobustXmlTrackChangesGenerator';
import { handleError } from '../../utils/errorHandling';
import { detectTextDirection } from '../../utils/textUtils';
import { directionForLanguage } from '../../i18n/config';
import './EditorView.css';
import { log, logError } from '@/utils/logging';
import { ProofreadWordAdapter } from '../../features/proofread/ProofreadWordAdapter';
import {
  getReviewPartyContextInstruction,
  hasReviewPartyContextComplete,
  resolveTrackChangeAuthorSide,
} from '../../utils/reviewPartyContext';
import { ooxmlToPartyTrackChangesHtml, prepareTrackChangesPreviewHtml } from '../../utils/trackChangePreviewHtml';
import {
  formatOoxmlParagraphForModel,
  markupToAcceptedPlainText,
} from '../../utils/trackChangesModelText';
import { AskMarkdown } from './AskMarkdown';
import './CrossCheckClauseCard.css';
import { isLegacyApplyEnabled, subscribeLegacyApplyEnabled } from '../../utils/addInPreferences';
import { applyViaHybridEngine } from '../../features/hybridApply/hybridEngine';
import { previewHybridOps } from '../../features/hybridApply/previewHybrid';
import {
  editorSuggestionTarget,
  looksLikeTrackChangesHtml,
  resolveSuggestedEditPreviewHtml,
  HYBRID_SUGGESTED_EDIT_PREVIEW_DEBOUNCE_MS,
} from '../../features/hybridApply/suggestedEdit';

/** Hybrid engine is the default apply path; the Profile "Legacy apply" toggle opts back to legacy. */
const isHybridApplyEnabled = (): boolean => !isLegacyApplyEnabled();

function EditorTrackChangesHtml({
  html,
  dir,
  processing = false,
}: {
  html: string;
  dir: string;
  processing?: boolean;
}) {
  return (
    <div
      className={`selected-text-full editor-panel-card${
        processing ? ' cc-client-request-content--ai-processing' : ''
      }`}
      dir={dir}
      aria-busy={processing || undefined}
    >
      <div className="cc-paragraph-xml-display" dir={dir}>
        <div
          className="cc-selected-text-xml"
          dir={dir}
          dangerouslySetInnerHTML={{ __html: html }}
        />
      </div>
    </div>
  );
}

function EditorPlainPreview({
  text,
  dir,
  processing = false,
}: {
  text: string;
  dir: string;
  processing?: boolean;
}) {
  return (
    <div
      className={`selected-text-full editor-panel-card${
        processing ? ' cc-client-request-content--ai-processing' : ''
      }`}
      dir={dir}
      aria-busy={processing || undefined}
    >
      <div className="selected-text-plain">{text}</div>
    </div>
  );
}

interface EditorViewProps {
  chatService: ChatService;
  onRequireReview?: () => void;
}

interface EditorResult {
  processedText: string;
  originalText: string;
  originalOoxml: string; // Locked original OOXML snapshot
  editedOoxml: string; // OOXML with track changes
  trackChangesHtml: string; // HTML for display
  originalParagraphIndex?: number; // Paragraph index at send time for precise Replace
  /** What the user asked (composer or quick action), for the explanation panel */
  userRequest: string;
  /** Short model explanation tied to the request (from ChatQueryResult.response or polish copy) */
  explanation: string;
}

export const EditorView: React.FC<EditorViewProps> = ({ chatService, onRequireReview }) => {
  const { t, i18n } = useTranslation('taskpane');
  const chromeDir = directionForLanguage(i18n.language);
  const selection = useSelection(chatService.getRephrasingService().getSelectionManager());
  const [result, setResult] = React.useState<EditorResult | null>(null);
  const [isProcessing, setIsProcessing] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);
  const [isStreaming, setIsStreaming] = React.useState(false);
  const [editedText, setEditedText] = React.useState<string>(''); // User-edited version of processed text
  const [draft, setDraft] = React.useState('');
  const [isReplacing, setIsReplacing] = React.useState(false);
  const [hybridEnabled, setHybridEnabled] = React.useState(isHybridApplyEnabled);
  /** Live-paragraph hybrid redline HTML — same source as MCC/CL Client Request preview. */
  const [hybridPreviewHtml, setHybridPreviewHtml] = React.useState<string | null>(null);
  /** Snapshot of selection HTML/text at send time — stays visible with Review-style shimmer. */
  const [streamLockedOriginal, setStreamLockedOriginal] = React.useState<{
    text: string;
    html: string | null;
    dir: 'ltr' | 'rtl';
  } | null>(null);
  /** Shown as soon as the user sends, including while the model is still streaming. */
  const [pendingUserRequest, setPendingUserRequest] = React.useState<string | null>(null);

  React.useEffect(() => {
    setHybridEnabled(isHybridApplyEnabled());
    return subscribeLegacyApplyEnabled(() => setHybridEnabled(isHybridApplyEnabled()));
  }, []);

  const hybridPreviewTarget = React.useMemo(() => {
    if (!result) return '';
    return editorSuggestionTarget({
      editedText,
      processedText: result.processedText,
    });
  }, [result, editedText]);

  const paragraphIndexForPreview =
    result?.originalParagraphIndex != null &&
    !Number.isNaN(Number(result.originalParagraphIndex)) &&
    Number(result.originalParagraphIndex) >= 0
      ? Number(result.originalParagraphIndex)
      : null;

  // Same read-only Word.run + Op[] renderer as MCC/CL Client Request (previewHybridOps).
  React.useEffect(() => {
    if (!hybridEnabled || paragraphIndexForPreview == null || !hybridPreviewTarget || !result) {
      setHybridPreviewHtml(null);
      return undefined;
    }
    let cancelled = false;
    const timer = window.setTimeout(() => {
      void previewHybridOps(paragraphIndexForPreview, hybridPreviewTarget).then((preview) => {
        if (cancelled) return;
        if (preview) {
          log('[EditorView] hybrid preview computed', {
            paragraphIndex: paragraphIndexForPreview,
            baselineLength: preview.baselineLength,
            targetLength: preview.targetLength,
            opCount: preview.ops.length,
          });
        } else {
          log('[EditorView] hybrid preview returned null', {
            paragraphIndex: paragraphIndexForPreview,
          });
        }
        setHybridPreviewHtml(preview?.html ?? null);
      });
    }, HYBRID_SUGGESTED_EDIT_PREVIEW_DEBOUNCE_MS);
    return () => {
      cancelled = true;
      window.clearTimeout(timer);
    };
  }, [hybridEnabled, paragraphIndexForPreview, hybridPreviewTarget, result]);

  // Debug: Log result state changes
  React.useEffect(() => {
    if (result) {
      console.log('[EditorView] Result state changed:', {
        hasResult: !!result,
        hasProcessedText: !!result.processedText,
        hasEditedOoxml: !!result.editedOoxml && result.editedOoxml.length > 0,
        hasTrackChangesHtml: !!result.trackChangesHtml && result.trackChangesHtml.length > 0,
        editedOoxmlLength: result.editedOoxml?.length || 0,
        trackChangesHtmlLength: result.trackChangesHtml?.length || 0,
        isStreaming,
        isProcessing,
        hasError: !!error
      });
    }
  }, [result, isStreaming, isProcessing, error]);

  // Check if Word.js is available
  const isWordAvailable = React.useMemo(() => {
    return typeof Word !== 'undefined' && typeof Word.run === 'function';
  }, []);

  // Create a stable key from OOXML by removing Word's auto-generated IDs
  // These IDs change on every refresh even when content is identical
  // Use a ref to cache normalized values to prevent unnecessary re-computation
  const normalizedCacheRef = React.useRef<Map<string, string>>(new Map());
  
  const ooxmlContentKey = React.useMemo(() => {
    const ooxml = selection?.ooxml;
    if (!ooxml || typeof ooxml !== 'string') return '';
    
    // Check cache first
    const cached = normalizedCacheRef.current.get(ooxml);
    if (cached !== undefined) {
      return cached;
    }
    
    // Remove Word-generated IDs that change on every refresh but don't affect content
    const normalized = ooxml
      .replace(/w14:paraId="[^"]*"/g, 'w14:paraId=""')
      .replace(/w14:textId="[^"]*"/g, 'w14:textId=""')
      .replace(/w:rsidR="[^"]*"/g, 'w:rsidR=""')
      .replace(/w:rsidRDefault="[^"]*"/g, 'w:rsidRDefault=""')
      .replace(/w:rsidRPr="[^"]*"/g, 'w:rsidRPr=""');
    
    // Cache the normalized result (limit cache size to prevent memory leaks)
    if (normalizedCacheRef.current.size > 10) {
      // Clear cache if it gets too large (keep only the most recent)
      const firstKey = normalizedCacheRef.current.keys().next().value;
      if (firstKey) {
        normalizedCacheRef.current.delete(firstKey);
      }
    }
    normalizedCacheRef.current.set(ooxml, normalized);
    
    return normalized;
  }, [selection?.ooxml]);

  // Memoize the HTML conversion to avoid re-computing on every render
  // Use normalized OOXML key to detect actual content changes
  const selectedTextHtml = React.useMemo(() => {
    if (!selection || !selection.ooxml || selection.ooxml.length === 0) {
      return null;
    }
    
    console.log('[EditorView] Converting OOXML to HTML (memoized)');
    console.log('[EditorView] OOXML length:', selection.ooxml.length);
    console.log('[EditorView] OOXML preview:', selection.ooxml.substring(0, 300));
    console.log('[EditorView] Selection has track changes:', selection.hasTrackChanges);
    
    return ooxmlToPartyTrackChangesHtml(selection.ooxml);
  }, [ooxmlContentKey]); // Use normalized key to detect actual content changes

  // Detect text direction for the selected text
  const selectedTextDirection = React.useMemo(() => {
    if (!selection || selection.isEmpty) {
      return 'ltr';
    }
    // Use the plain text to detect direction
    return detectTextDirection(selection.text);
  }, [selection?.text]);

  // Live selection tracking only while Editor is visible — avoids a full-document
  // Word.run on every keystroke when the user is on Cross-Check or other tabs.
  React.useEffect(() => {
    if (!isWordAvailable) {
      return;
    }

    const rephrasingService = chatService.getRephrasingService();
    rephrasingService.startSelectionTracking();
    rephrasingService.refreshSelection().catch(() => {
      // Silently handle errors
    });

    return () => {
      rephrasingService.stopSelectionTracking();
    };
  }, [chatService, isWordAvailable]);

  // Do not apply any add-in highlight: desired state is Word's native selection only
  // (the "blue" in the reference screenshot is Word's selection, not a format we set)

  const handleSendMessage = async (messageText: string) => {
    if (!selection || selection.isEmpty) {
      setError(t('editor.selectBeforeSend'));
      return;
    }

    if (!hasReviewPartyContextComplete()) {
      onRequireReview?.();
      return;
    }

    const trimmedRequest = messageText.trim();
    setPendingUserRequest(trimmedRequest);
    setError(null);
    setResult(null);
    setEditedText('');
    // Paint the original immediately so the Review-style shimmer never flashes «מעבד…».
    setStreamLockedOriginal({
      text: selection.text,
      html: selectedTextHtml,
      dir: selectedTextDirection,
    });
    setIsProcessing(true);
    setIsStreaming(true);

    // STAGE 1: IDENTIFICATION - Lock the selection snapshot at send time.
    // Live tracking uses getSelectionLight() which omits paragraphIndex; refresh the
    // full selection so Replace has a real body.paragraphs index even on redlines.
    let lockedSource = selection;
    try {
      lockedSource = await DocumentReader.getSelectionOrParagraph();
    } catch (err) {
      logError('[EditorView] Failed to refresh selection at send', err);
    }
    const locked = {
      text: lockedSource.text || selection.text,
      ooxml: lockedSource.ooxml || selection.ooxml || '',
      paragraphIndex: lockedSource.paragraphIndex ?? selection.paragraphIndex,
    };
    const markedForModel =
      (locked.ooxml
        ? formatOoxmlParagraphForModel(locked.ooxml, resolveTrackChangeAuthorSide)
        : '') || locked.text;
    
      log('[EditorView] Locked selection snapshot:', {
      textLength: locked.text.length,
      markedForModelLength: markedForModel.length,
      paragraphIndex: locked.paragraphIndex,
      textPreview: markedForModel.substring(0, 100)
    });

    let lockedDisplayHtml: string | null = null;
    if (locked.ooxml && locked.ooxml.length > 0) {
      lockedDisplayHtml = ooxmlToPartyTrackChangesHtml(locked.ooxml);
    }
    setStreamLockedOriginal({
      text: locked.text,
      html: lockedDisplayHtml,
      dir: detectTextDirection(locked.text),
    });

    try {
      // Process the query with streaming
      log('[EditorView] Starting streaming query processing');
      let chunkCount = 0;
      let accumulatedText = ''; // Local accumulator like in ChatPage and ClauseCard
      
      // Use locked snapshot text for processing
      for await (const chunk of chatService.processQueryStream(messageText, markedForModel)) {
        chunkCount++;
        log(`[EditorView] Received chunk #${chunkCount}:`, {
          type: chunk.type,
          hasContent: !!chunk.content,
          contentLength: chunk.content?.length || 0,
          hasResult: !!chunk.result,
          hasError: !!chunk.error
        });
        
        if (chunk.type === 'text' && chunk.content) {
          accumulatedText += chunk.content;
          log(`[EditorView] Accumulated text length: ${accumulatedText.length} chars`);
        } else if (chunk.type === 'complete' && chunk.result) {
          // Streaming complete - process final result
          const queryResult = chunk.result;
          log('[EditorView] Streaming complete, processing final result');
          
          setIsStreaming(false);
          
          const processedText =
            markupToAcceptedPlainText(queryResult.processedText || '').trim() ||
            (queryResult.processedText || '').trim();
          if (processedText) {
            log('[EditorView] Generating XML with track changes');
            log('[EditorView] Original OOXML available (locked):', !!locked.ooxml);
            log('[EditorView] Processed text length:', processedText.length);

            // STAGE 2: COMPARISON - Generate XML with track changes using locked snapshot
            let editedOoxml = '';
            let trackChangesHtml = '';
            
            if (locked.ooxml && locked.ooxml.length > 0) {
              try {
                console.log('[EditorView] Starting XML generation with locked OOXML...', {
                  ooxmlLength: locked.ooxml.length,
                  processedTextLength: processedText.length,
                  ooxmlPreview: locked.ooxml.substring(0, 200)
                });
                
                // Generate OOXML with track changes using locked original OOXML
                // Use 'AI Suggestion' as author so new suggestions get blue styling (track-change-new class)
                editedOoxml = RobustXmlTrackChangesGenerator.generateTrackChangesXml(
                  locked.ooxml,
                  processedText,
                  'AI Suggestion'
                );
                
                console.log('[EditorView] Generated edited OOXML:', {
                  length: editedOoxml.length,
                  isEmpty: editedOoxml.length === 0,
                  preview: editedOoxml.substring(0, 200)
                });
                
                if (editedOoxml && editedOoxml.length > 0) {
                  trackChangesHtml = ooxmlToPartyTrackChangesHtml(editedOoxml) || processedText;
                  console.log('[EditorView] Generated track changes HTML:', {
                    length: trackChangesHtml.length,
                    isEmpty: trackChangesHtml.length === 0,
                    preview: trackChangesHtml.substring(0, 200),
                    hasTrackChanges: trackChangesHtml.includes('track-change')
                  });
                } else {
                  console.warn('[EditorView] Generated OOXML is empty, falling back to plain text');
                  trackChangesHtml = processedText;
                }
              } catch (error) {
                console.error('[EditorView] Error generating track changes XML:', error);
                console.error('[EditorView] Error details:', {
                  message: error instanceof Error ? error.message : String(error),
                  stack: error instanceof Error ? error.stack : undefined
                });
                // Fallback: use plain text
                trackChangesHtml = processedText;
              }
            } else {
              console.warn('[EditorView] No OOXML available, using plain text');
              trackChangesHtml = processedText;
            }

            console.log('[EditorView] Setting result:', {
              hasProcessedText: !!processedText,
              hasEditedOoxml: !!editedOoxml && editedOoxml.length > 0,
              hasTrackChangesHtml: !!trackChangesHtml && trackChangesHtml.length > 0,
              editedOoxmlLength: editedOoxml.length,
              trackChangesHtmlLength: trackChangesHtml.length
            });

            const explanation =
              (queryResult.response && queryResult.response.trim()) ||
              t('editor.defaultExplanation');

            // body.paragraphs exact-match can miss (partial selection, track changes,
            // Hebrew/bidi). MCC Submit resolves via content search — do the same so
            // hybrid preview and Replace share a real body index.
            let paragraphIndex = locked.paragraphIndex;
            if (paragraphIndex == null || Number.isNaN(Number(paragraphIndex)) || Number(paragraphIndex) < 0) {
              const searchText =
                (locked.ooxml
                  ? TrackChangesParser.extractAcceptedText(locked.ooxml)
                  : locked.text) || locked.text;
              const resolved = await DocumentReader.resolveParagraphForApply(undefined, {
                searchText,
                searchAnchors: [
                  locked.text,
                  locked.ooxml ? TrackChangesParser.extractOriginalText(locked.ooxml) : '',
                ],
              });
              if (resolved != null) {
                paragraphIndex = resolved;
                log('[EditorView] Resolved paragraph index via content search', {
                  paragraphIndex: resolved,
                });
              }
            }

            // Set all state together to avoid intermediate renders
            // Store locked snapshot in result for use in Replace
            setResult({
              processedText,
              originalText: locked.text,
              originalOoxml: locked.ooxml,
              editedOoxml: editedOoxml,
              trackChangesHtml: trackChangesHtml,
              originalParagraphIndex: paragraphIndex,
              userRequest: trimmedRequest,
              explanation,
            });
            
            // Initialize edited text with processed text (user can edit it)
            setEditedText(processedText);
            setStreamLockedOriginal(null);

            // Ensure processing and streaming flags are cleared
            setIsProcessing(false);
            setIsStreaming(false);
            
            console.log('[EditorView] Result set, flags cleared:', {
              hasResult: true,
              isProcessing: false,
              isStreaming: false
            });
          } else {
            setError(t('editor.noProcessedText'));
            setIsProcessing(false);
            setIsStreaming(false);
            setStreamLockedOriginal(null);
            setPendingUserRequest(null);
          }
        } else if (chunk.type === 'error') {
          // Error during streaming
          setError(chunk.error || t('editor.processingError'));
          setIsStreaming(false);
          setIsProcessing(false);
          setStreamLockedOriginal(null);
          setPendingUserRequest(null);
        }
      }
    } catch (err) {
      setError(handleError(err));
      setResult(null);
      setEditedText('');
      setIsStreaming(false);
      setIsProcessing(false);
      setStreamLockedOriginal(null);
      setPendingUserRequest(null);
    }
  };

  const handleReplace = async () => {
    if (!result || isReplacing) return;

    try {
      setIsReplacing(true);
      setError(null);
      const textToApply =
        hybridPreviewTarget ||
        editorSuggestionTarget({
          editedText,
          processedText: result.processedText,
        });
      const searchText =
        (result.originalOoxml
          ? TrackChangesParser.extractAcceptedText(result.originalOoxml)
          : result.originalText) || result.originalText;
      const searchAnchors = [
        result.originalText,
        result.originalOoxml ? TrackChangesParser.extractOriginalText(result.originalOoxml) : '',
      ];
      // Same as MCC Submit: re-resolve body index by content. The locked snapshot
      // often has paragraphIndex=null (partial selection / track changes), but
      // content search still finds the paragraph — hybrid needs that index.
      let paragraphIndex = result.originalParagraphIndex;
      const resolved = await DocumentReader.resolveParagraphForApply(paragraphIndex, {
        searchText,
        searchAnchors,
      });
      if (resolved != null) {
        paragraphIndex = resolved;
        if (result.originalParagraphIndex !== resolved) {
          setResult((prev) =>
            prev ? { ...prev, originalParagraphIndex: resolved } : prev,
          );
        }
      } else if (paragraphIndex == null || Number.isNaN(Number(paragraphIndex)) || Number(paragraphIndex) < 0) {
        try {
          const liveSel = await DocumentReader.getSelectionOrParagraph();
          if (liveSel.paragraphIndex != null && liveSel.paragraphIndex >= 0) {
            paragraphIndex = liveSel.paragraphIndex;
            log('[EditorView] Using current selection paragraph index after resolve miss', {
              paragraphIndex,
            });
          }
        } catch (err) {
          logError('[EditorView] Failed to read current selection for apply', err);
        }
      }
      const hasParagraphIndex =
        paragraphIndex != null && !Number.isNaN(Number(paragraphIndex)) && Number(paragraphIndex) >= 0;
      const useHybridApply = isHybridApplyEnabled();
      const useIncremental =
        result.trackChangesHtml.length > 0 && looksLikeTrackChangesHtml(result.trackChangesHtml);

      let applyResult = { applied: false };

      // Same ladder as MCC/CL Submit-to-Response: hybrid only when enabled (no OOXML/incremental
      // fallback — a hybrid miss is a clean no-op). Legacy toggle uses insertOoxml then incremental.
      if (useHybridApply && hasParagraphIndex) {
        const hybrid = await applyViaHybridEngine(Number(paragraphIndex), textToApply);
        applyResult = { applied: hybrid.applied };
        log('[EditorView] Replace via hybrid engine', {
          applied: hybrid.applied,
          reason: hybrid.reason ?? null,
          trace: hybrid.trace,
        });
      } else if (result.editedOoxml) {
        applyResult = await RedlineInserter.applyParagraphFromEditedOoxml(
          result.editedOoxml,
          paragraphIndex,
          { searchText, searchAnchors },
        );
        log('[EditorView] Replace via edited OOXML', { applied: applyResult.applied });
      }

      if (!useHybridApply && !applyResult.applied && useIncremental) {
        applyResult = await RedlineInserter.applyWordLevelChangesIncrementally(
          result.originalText,
          textToApply,
          result.trackChangesHtml,
          paragraphIndex,
        );
        log('[EditorView] Replace via incremental search', { applied: applyResult.applied });
      }

      if (!applyResult.applied) {
        setError(t('editor.applyFailed'));
        return;
      }

      log('[EditorView] Text replacement completed successfully');
    } catch (err) {
      logError('[EditorView] Error replacing text:', err);
      setError(t('editor.applyFailedDetail', { detail: handleError(err) }));
    } finally {
      setIsReplacing(false);
    }
  };

  const handleCopy = () => {
    if (!result) return;
    const textToCopy = editedText.trim() || result.processedText;
    navigator.clipboard.writeText(textToCopy);
  };

  function handleNavigateToSelection() {
    if (!result) return;
    if (result.originalParagraphIndex != null && result.originalParagraphIndex >= 0) {
      void ProofreadWordAdapter.navigateToParagraph(result.originalParagraphIndex);
    } else {
      void ProofreadWordAdapter.navigateByContent(result.originalText);
    }
  }

  /** Dismiss suggested edit in the panel but keep the current Word selection */
  function handleDismissSuggestion() {
    setResult(null);
    setEditedText('');
    setError(null);
    setPendingUserRequest(null);
  }

  const handleQuickAction = (kind: 'favor' | 'mutual' | 'polish') => {
    if (kind === 'favor') {
      if (!getReviewPartyContextInstruction()) {
        setError(t('editor.completeReviewFirst'));
        return;
      }
      void handleSendMessage('favor my party');
      return;
    }
    if (kind === 'mutual') {
      void handleSendMessage('make it mutual');
      return;
    }
    void handleSendMessage('polish');
  };

  function clearSelectionAndResult() {
    chatService.getRephrasingService().clearSelection();
    setResult(null);
    setEditedText('');
    setError(null);
    setIsStreaming(false);
    setIsProcessing(false);
    setStreamLockedOriginal(null);
    setPendingUserRequest(null);
  }

  const hasSelection = !!(selection && !selection.isEmpty);
  const showBusy = isStreaming || isProcessing;
  const busyPreviewHtml = streamLockedOriginal?.html ?? selectedTextHtml;
  const busyPreviewText = streamLockedOriginal?.text ?? selection?.text ?? '';
  const busyPreviewDir = streamLockedOriginal?.dir ?? selectedTextDirection;

  const renderRequestMeta = (request: string, explanation?: string | null) => (
    <div className="editor-suggested-meta">
      <div className="editor-suggested-request" dir={detectTextDirection(request)}>
        <span className="editor-suggested-explanation-label">{t('editor.yourRequest')}</span>
        <p className="editor-suggested-explanation-value" title={request}>
          {request}
        </p>
      </div>
      {explanation ? (
        <div className="editor-suggested-summary">
          <span className="editor-suggested-explanation-label">{t('editor.summary')}</span>
          <div className="editor-suggested-summary-md">
            <AskMarkdown content={explanation} dir={detectTextDirection(explanation)} />
          </div>
        </div>
      ) : null}
    </div>
  );

  return (
    <div className="editor-view" dir={chromeDir}>
      {/* Single main canvas: selection OR streaming OR suggested edit (no duplicate blocks) */}
      <div className="editor-main-area">
        {error && (
          <div className="error-state editor-main-error" role="alert">
            <span className="error-icon">⚠️</span>
            <span>{error}</span>
          </div>
        )}

        {showBusy ? (
          <div className="editor-panel editor-panel--streaming">
            <div className="editor-panel-header">
              <span className="editor-panel-title">{t('editor.selection')}</span>
              <button
                type="button"
                className="editor-panel-dismiss"
                onClick={clearSelectionAndResult}
                title={t('editor.dismiss')}
              >
                ✕
              </button>
            </div>
            {busyPreviewHtml ? (
              <EditorTrackChangesHtml html={busyPreviewHtml} dir={busyPreviewDir} processing />
            ) : (
              <EditorPlainPreview text={busyPreviewText} dir={busyPreviewDir} processing />
            )}
            {pendingUserRequest ? renderRequestMeta(pendingUserRequest) : null}
          </div>
        ) : result ? (
          <div className="editor-panel editor-panel--result">
            <div className="editor-panel-header">
              <span className="editor-panel-title">{t('editor.suggestedEdit')}</span>
              <button
                type="button"
                className="editor-panel-dismiss"
                onClick={clearSelectionAndResult}
                title={t('editor.dismiss')}
              >
                ✕
              </button>
            </div>
            {(() => {
              // MCC/CL Client Request: hybrid ops HTML when available, else OOXML→HTML fallback.
              const effectivePreviewHtml = resolveSuggestedEditPreviewHtml({
                hybridEnabled,
                hybridPreviewHtml,
                fallbackHtml: result.trackChangesHtml,
              });
              const previewHtml =
                effectivePreviewHtml && looksLikeTrackChangesHtml(effectivePreviewHtml)
                  ? prepareTrackChangesPreviewHtml(effectivePreviewHtml)
                  : null;
              const previewDir = result.processedText
                ? detectTextDirection(result.processedText)
                : 'ltr';

              return previewHtml ? (
                <EditorTrackChangesHtml html={previewHtml} dir={previewDir} />
              ) : (
                <div className="selected-text-full editor-panel-card">
                  <div className="selected-text-plain">{editedText || result.processedText}</div>
                </div>
              );
            })()}
            <div className="editor-suggested-actions">
              <button
                type="button"
                className="editor-replace-btn"
                onClick={handleReplace}
                disabled={isReplacing}
                title={t('editor.applyInDocument')}
              >
                {isReplacing ? t('editor.replacing') : t('editor.replace')}
              </button>
              <button
                type="button"
                className="editor-icon-btn"
                title={t('editor.navigateParagraph')}
                onClick={handleNavigateToSelection}
              >
                <Eye size={12} />
              </button>
              <button type="button" className="editor-icon-btn" title={t('editor.copySuggestion')} onClick={handleCopy}>
                <Copy size={12} />
              </button>
              <button
                type="button"
                className="editor-icon-btn editor-ignore-btn"
                title={t('editor.dismissSuggestion')}
                onClick={handleDismissSuggestion}
              >
                <Minus size={12} />
              </button>
            </div>
            {renderRequestMeta(result.userRequest, result.explanation)}
          </div>
        ) : hasSelection ? (
          <div className="editor-panel editor-panel--selection">
            <div className="editor-panel-header">
              <span className="editor-panel-title">{t('editor.selection')}</span>
              <button
                type="button"
                className="editor-panel-dismiss"
                onClick={clearSelectionAndResult}
                title={t('editor.clearSelection')}
              >
                ✕
              </button>
            </div>
            {selectedTextHtml ? (
              <EditorTrackChangesHtml html={selectedTextHtml} dir={selectedTextDirection} />
            ) : (
              <EditorPlainPreview text={selection!.text} dir={selectedTextDirection} />
            )}
          </div>
        ) : (
          <div className="empty-state">
            <MessageCircle className="empty-icon" size={48} />
            <p>{t('editor.emptyTitle')}</p>
            <p className="example-text">{t('editor.emptyHint')}</p>
          </div>
        )}
      </div>

      {/* Quick Actions - Near Chat Input */}
      <div className="quick-actions-bottom">
        <button 
          className="quick-action-btn"
          onClick={() => handleQuickAction('favor')}
          disabled={!selection || selection.isEmpty || isProcessing}
        >
          {t('editor.favorMyParty')}
        </button>
        <button 
          className="quick-action-btn"
          onClick={() => handleQuickAction('mutual')}
          disabled={!selection || selection.isEmpty || isProcessing}
        >
          {t('editor.makeMutual')}
        </button>
        <button 
          className="quick-action-btn"
          onClick={() => handleQuickAction('polish')}
          disabled={!selection || selection.isEmpty || isProcessing}
        >
          {t('editor.polish')}
        </button>
      </div>

      {/* Chat composer (same design as Agent / Ask) */}
      <div className="editor-chat-area">
        <ChatComposer
          value={draft}
          onChange={setDraft}
          onSubmit={async ({ text }) => {
            const trimmed = text.trim();
            if (!trimmed || isProcessing) return;
            if (!selection || selection.isEmpty) {
              setError(t('editor.selectBeforeSend'));
              return;
            }
            setDraft('');
            await handleSendMessage(trimmed);
          }}
          disabled={isProcessing || !selection || selection.isEmpty}
          submitting={isProcessing}
          placeholder={
            !selection || selection.isEmpty
              ? t('editor.selectTextToStart')
              : t('editor.askCourty')
          }
          layout="sticky"
          label={
            <div className="ag-composer-label">
              <PencilLine size={14} aria-hidden />
              <span>{t('editor.editSelectedText')}</span>
            </div>
          }
          followUp={!!result || isProcessing || isStreaming}
          submitTitle={t('editor.sendEditRequest')}
          showAgentModeSelector={false}
        />
      </div>
    </div>
  );
};

